/* motion.js */
/* Ekologa — motion.js · vanilla, 0 deps. Compositor-only, reduced-motion-safe.
   Each module self-guards: no matching elements => no-op. Loaded once via <script defer> in head. */
(function () {
  'use strict';
  var reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* 1) Scroll-Reveal — variants via CSS custom props, optional stagger via --rv-i */
  function initReveal() {
    var els = document.querySelectorAll('.reveal');
    if (!els.length) return;
    if (reduce) { els.forEach(function (el) { el.classList.add('is-visible', 'reveal-done'); }); return; }
    document.querySelectorAll('[data-stagger]').forEach(function (group) {
      var i = 0;
      group.querySelectorAll(':scope > .reveal').forEach(function (child) {
        if (!child.hasAttribute('data-delay')) child.style.setProperty('--rv-i', i++);
      });
    });
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (!e.isIntersecting) return;
        var el = e.target;
        el.classList.add('is-visible');
        io.unobserve(el);
        var done = function () { el.classList.add('reveal-done'); el.removeEventListener('transitionend', done); };
        el.addEventListener('transitionend', done);
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -40px 0px' });
    els.forEach(function (el) { io.observe(el); });
  }

  /* 2) Number counters [data-count-to] — de-DE format, easeOutCubic, count once on scroll-in */
  function initCounters() {
    var els = document.querySelectorAll('[data-count-to]');
    if (!els.length || reduce) return; /* visible text is already the final value */
    function fmt(n, dec) {
      return new Intl.NumberFormat('de-DE', { minimumFractionDigits: dec, maximumFractionDigits: dec }).format(n);
    }
    function run(el) {
      var target = parseFloat(el.dataset.countTo);
      var dec = parseInt(el.dataset.decimals || '0', 10);
      var suffix = el.dataset.suffix || '';
      var prefix = el.dataset.prefix || '';
      var dur = parseInt(el.dataset.countDur || '1800', 10);
      var t0 = performance.now();
      function step(t) {
        var p = Math.min(1, (t - t0) / dur);
        var eased = 1 - Math.pow(1 - p, 3);
        el.textContent = prefix + fmt(target * eased, dec) + suffix;
        if (p < 1) requestAnimationFrame(step);
        else el.textContent = prefix + fmt(target, dec) + suffix;
      }
      requestAnimationFrame(step);
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) { if (e.isIntersecting) { run(e.target); io.unobserve(e.target); } });
    }, { threshold: 0.4 });
    els.forEach(function (el) { io.observe(el); });
  }

  /* 3) Tap-ripple on primary tap targets (scale+opacity only) */
  function initRipple() {
    if (reduce) return;
    document.querySelectorAll('.btn-primary, .btn-accent, .msc-cta, .size-tile').forEach(function (host) {
      host.classList.add('ripple-host');
      host.addEventListener('pointerdown', function (e) {
        var r = host.getBoundingClientRect();
        var size = Math.max(r.width, r.height);
        var s = document.createElement('span');
        s.className = 'ripple';
        s.style.width = s.style.height = size + 'px';
        s.style.left = (e.clientX - r.left - size / 2) + 'px';
        s.style.top = (e.clientY - r.top - size / 2) + 'px';
        host.appendChild(s);
        s.addEventListener('animationend', function () { s.remove(); });
      }, { passive: true });
    });
  }

  /* 4) Subtle scroll-parallax [data-parallax] — translateY only, rAF-throttled */
  function initParallax() {
    if (reduce) return;
    var els = Array.prototype.slice.call(document.querySelectorAll('[data-parallax]'));
    if (!els.length) return;
    var mobile = innerWidth < 768;
    var ticking = false;
    function update() {
      var vh = innerHeight;
      els.forEach(function (el) {
        var r = el.getBoundingClientRect();
        var center = r.top + r.height / 2 - vh / 2;
        var speed = parseFloat(el.dataset.parallaxSpeed || '0.05');
        if (mobile) speed *= 0.5;
        el.style.transform = 'translate3d(0,' + (-center * speed).toFixed(2) + 'px,0)';
      });
      ticking = false;
    }
    function onScroll() { if (!ticking) { ticking = true; requestAnimationFrame(update); } }
    addEventListener('scroll', onScroll, { passive: true });
    addEventListener('resize', onScroll, { passive: true });
    update();
  }

  /* 5) Sticky-scroll story [data-story] — IO step-switch (no per-frame scrubbing) */
  function initStory() {
    document.querySelectorAll('[data-story]').forEach(function (story) {
      var stage = story.querySelector('.story-stage');
      var steps = story.querySelectorAll('[data-story-step]');
      if (!stage || !steps.length) return;
      if (reduce) { stage.setAttribute('data-active', 'all'); return; }
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) { if (e.isIntersecting) stage.setAttribute('data-active', e.target.dataset.storyStep); });
      }, { rootMargin: '-45% 0px -45% 0px', threshold: 0 });
      steps.forEach(function (s) { io.observe(s); });
    });
  }

  function init() { initReveal(); initCounters(); initRipple(); initParallax(); initStory(); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();


/* ════════════════════════════════════════════════════════════════
   Ekologa Commerce Layer — echte Shopify Cart AJAX API (0 deps)
   Ersetzt die frühere localStorage-Demo. Alle Buttons führen jetzt
   reale Warenkorb-Operationen aus (/cart/add.js, /cart/change.js).
   ════════════════════════════════════════════════════════════════ */
(function () {
  'use strict';

  /* ── Geld-Formatierung (de-DE, EUR) ───────────────────────────── */
  function money(cents) {
    return new Intl.NumberFormat('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      .format(cents / 100) + ' €';
  }

  /* ── Cart-Kern ────────────────────────────────────────────────── */
  function getCart() {
    return fetch('/cart.js', { credentials: 'same-origin', headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); });
  }

  function postJSON(url, payload) {
    return fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(payload)
    }).then(function (r) {
      /* Nicht-JSON-Antworten (z. B. 429/502-HTML bei Throttle) sauber abfangen —
         deutscher Hinweis statt englischem SyntaxError; die Zeile entsperrt der Aufrufer im catch. */
      return r.json().catch(function () {
        throw new Error('Kurz zu viele Anfragen — bitte in ein paar Sekunden erneut versuchen.');
      }).then(function (data) {
        if (!r.ok) { var err = new Error(data.description || data.message || 'Fehler'); err.data = data; throw err; }
        return data;
      });
    });
  }

  function renderCount(n) {
    document.querySelectorAll('[data-cart-count]').forEach(function (c) {
      c.textContent = n;
      c.style.display = n > 0 ? 'flex' : 'none';
    });
  }

  function refreshCount() {
    return getCart().then(function (c) { renderCount(c.item_count); return c; }).catch(function () {});
  }

  /* ── Toast ────────────────────────────────────────────────────── */
  var toastTimer = null;
  function toast(title, meta) {
    var t = document.querySelector('[data-cart-toast]');
    if (!t) return;
    var titleEl = t.querySelector('[data-toast-title]');
    var metaEl = t.querySelector('[data-toast-meta]');
    if (titleEl) titleEl.textContent = title;
    if (metaEl) metaEl.textContent = meta || '';
    t.classList.add('is-visible');
    if (toastTimer) clearTimeout(toastTimer);
    toastTimer = setTimeout(function () { t.classList.remove('is-visible'); }, 4000);
  }

  /* ── Cart-Section-Re-Render ──────────────────────────────────
     Ersetzt die Warenkorb-Section aus dem sections-Feld einer Cart-AJAX-Antwort
     (Shopify Section Rendering) — derselbe Pfad, den der Mengen-Stepper nutzt.
     true = Section wurde ersetzt. */
  function applySectionHTML(sectionId, data) {
    if (!sectionId || !data || !data.sections || !data.sections[sectionId]) return false;
    var host = document.getElementById('shopify-section-' + sectionId);
    if (!host) return false;
    var tmp = document.createElement('div');
    tmp.innerHTML = data.sections[sectionId];
    var fresh = tmp.querySelector('#shopify-section-' + sectionId);
    host.innerHTML = fresh ? fresh.innerHTML : data.sections[sectionId];
    return true;
  }

  /* ── Add to Cart ──────────────────────────────────────────────── */
  function addItem(item, opts) {
    opts = opts || {};
    var payload = { items: [item] };
    if (opts.sections) payload.sections = opts.sections;
    return postJSON('/cart/add.js', payload).then(function (data) {
      /* Gleiche Abo-Variante+Plan verschmilzt mit einer bestehenden Zeile (z. B. 2+2=4) —
         danach „Du sparst"/„Inhalt" wie beim Warenkorb-Stepper für die NEUE Menge neu
         berechnen (syncAboProps). Ein Property-Fehler blockiert den Kauf nie. */
      return refreshCount().then(function (cart) {
        return cart ? syncAboProps(cart) : null;
      }).catch(function () {}).then(function () {
        /* Auf der Warenkorb-Seite (Upsell-Quick-Add): Section sofort neu rendern —
           Zeilen, Zwischensumme und Versand-Fortschritt stimmen ohne Reload. */
        applySectionHTML(opts.sections, data);
        if (!opts.silent) {
          var added = data.items && data.items[0];
          var title = opts.title || (added ? added.product_title : 'Produkt');
          var meta = opts.meta || (added ? (item.quantity > 1 ? item.quantity + ' × ' : '') + money(added.final_price) : '');
          toast(title, meta);
        }
        if (opts.redirect) window.location.href = opts.redirect;
        return data;
      });
    });
  }

  /* Liest den Kauf-Zustand aus der Buybox (von der PDP-Logik gepflegt) */
  function buyboxState() {
    var box = document.querySelector('[data-pdp-buybox]');
    if (!box || !box.dataset.variantId) return null;
    var item = { id: parseInt(box.dataset.variantId, 10), quantity: parseInt(box.dataset.qty || '1', 10) };
    if (box.dataset.sellingPlan) {
      item.selling_plan = parseInt(box.dataset.sellingPlan, 10);
      /* Sichtbare Properties (Du sparst / Inhalt) nur bei Abo-Positionen — Shopify zeigt
         sie in Checkout + Bestellbestätigung. Ein Property-Fehler blockiert den Kauf nie. */
      if (box.dataset.cartProps) { try { item.properties = JSON.parse(box.dataset.cartProps); } catch (e) { /* noop */ } }
    }
    return { item: item, title: box.dataset.cartTitle || document.title, meta: box.dataset.cartMeta || '' };
  }

  function busy(btn, on) {
    if (!btn) return;
    if (on) { btn.dataset.busy = '1'; btn.setAttribute('aria-busy', 'true'); btn.style.opacity = '0.6'; }
    else { btn.dataset.busy = ''; btn.removeAttribute('aria-busy'); btn.style.opacity = ''; }
  }

  /* Delegierte Klicks: PDP-Add, Quick-Add (Karten), Buy-Now.
     [data-msc-add] ist NICHT dabei — die Sticky-Bar proxyt auf [data-pdp-add]. */
  document.addEventListener('click', function (e) {
    var quick = e.target.closest('[data-quick-add]');
    if (quick) {
      e.preventDefault();
      if (quick.dataset.busy === '1') return;
      busy(quick, true);
      var quickRoot = cartRoot();
      addItem(
        { id: parseInt(quick.dataset.variantId, 10), quantity: parseInt(quick.dataset.quantity || '1', 10) },
        { title: quick.dataset.title || 'Produkt', sections: quickRoot ? quickRoot.dataset.sectionId : null }
      ).catch(function (err) { toast('Konnte nicht hinzugefügt werden', err.message); })
       .finally(function () { busy(quick, false); });
      return;
    }

    var addBtn = e.target.closest('[data-pdp-add]');
    if (addBtn) {
      e.preventDefault();
      if (addBtn.dataset.busy === '1') return;
      var state = buyboxState();
      if (!state) return;
      busy(addBtn, true);
      addItem(state.item, { title: state.title, meta: state.meta })
        .catch(function (err) { toast('Konnte nicht hinzugefügt werden', err.message); })
        .finally(function () { busy(addBtn, false); });
      return;
    }

    var buyNow = e.target.closest('[data-pdp-buy-now]');
    if (buyNow) {
      e.preventDefault();
      if (buyNow.dataset.busy === '1') return;
      var s2 = buyboxState();
      if (!s2) return;
      busy(buyNow, true);
      addItem(s2.item, { silent: true, redirect: '/checkout' })
        .catch(function (err) { toast('Konnte nicht hinzugefügt werden', err.message); busy(buyNow, false); });
      return;
    }
  });

  /* ── Cart-Seite: Menge ändern / entfernen (delegiert, Section-Reload) ── */
  function cartRoot() { return document.querySelector('[data-cart-root]'); }

  /* Die Abo-Properties „Du sparst"/„Inhalt" hängen von der Menge ab — bei Mengen-Änderung
     im Warenkorb centgenau aus der Selling-Plan-Allocation neu berechnen, sonst stünde im
     Checkout ein veralteter Betrag. */
  function updatedAboProps(item, quantity) {
    var props = item.properties || {};
    if (!item.selling_plan_allocation || !props['Du sparst']) return null;
    var unit = item.selling_plan_allocation.compare_at_price || item.original_price;
    var per = unit - item.selling_plan_allocation.price;
    if (per <= 0) return null;
    var pct = Math.round(per * 100 / unit);
    var out = {};
    for (var k in props) out[k] = props[k];
    out['Du sparst'] = ((per * quantity) / 100).toFixed(2).replace('.', ',') + ' € (−' + pct + ' %)';
    if (out['Inhalt']) {
      var inh = String(out['Inhalt']).replace(/^\d+/, String(quantity));
      var m = inh.match(/×\s*(\d+)/);
      if (m) inh = inh.replace(/=\s*\d+\s*$/, '= ' + quantity * parseInt(m[1], 10));
      out['Inhalt'] = inh;
    }
    return out;
  }

  /* Nach /cart/add.js: eine verschmolzene Abo-Zeile trägt noch die Properties der alten
     Menge → jede Abo-Zeile mit „Du sparst" über DENSELBEN Rechenweg (updatedAboProps)
     auf ihre aktuelle line.quantity bringen. Nur bei echter Abweichung wird gePOSTet. */
  function syncAboProps(cart) {
    var chain = Promise.resolve();
    (cart.items || []).forEach(function (item) {
      var np = updatedAboProps(item, item.quantity);
      var cur = item.properties || {};
      if (!np || (np['Du sparst'] === cur['Du sparst'] && np['Inhalt'] === cur['Inhalt'])) return;
      chain = chain.then(function () {
        return postJSON('/cart/change.js', { id: item.key, quantity: item.quantity, properties: np });
      });
    });
    return chain;
  }

  /* Cart-Mutation über den stabilen Line-Item-Key (item.key) statt Zeilen-Index —
     /cart/add.js stellt neue Positionen VORAN, dadurch verschieben sich Indizes;
     der Key bleibt und trifft immer die richtige Zeile. */
  function changeLine(key, quantity) {
    var root = cartRoot();
    var sectionId = root ? root.dataset.sectionId : null;
    return getCart().then(function (cart) {
      var payload = { id: key, quantity: quantity };
      if (sectionId) payload.sections = sectionId;
      var item = null;
      if (cart && cart.items) {
        for (var i = 0; i < cart.items.length; i++) {
          if (cart.items[i].key === key) { item = cart.items[i]; break; }
        }
      }
      if (item && quantity > 0) {
        var np = updatedAboProps(item, quantity);
        if (np) payload.properties = np;
      }
      return postJSON('/cart/change.js', payload);
    }).then(function (data) {
      renderCount(data.item_count);
      if (!applySectionHTML(sectionId, data) && root) {
        window.location.reload();
      }
      return data;
    });
  }

  document.addEventListener('click', function (e) {
    var root = cartRoot();
    if (!root) return;

    var qtyBtn = e.target.closest('[data-qty]');
    if (qtyBtn && root.contains(qtyBtn)) {
      e.preventDefault();
      var itemEl = qtyBtn.closest('[data-key]');
      if (!itemEl || itemEl.dataset.busy === '1') return;
      var key = itemEl.dataset.key;
      var current = parseInt(itemEl.dataset.quantity, 10);
      var minQ = parseInt(itemEl.dataset.min, 10) || 0;
      var next = qtyBtn.dataset.qty === '+' ? current + 1 : current - 1;
      if (qtyBtn.dataset.qty === '-' && next < minQ) return; /* Abo: nicht unter Mindestmenge (2) */
      if (next < 0) next = 0;
      itemEl.dataset.busy = '1';
      itemEl.style.opacity = '0.5';
      changeLine(key, next).catch(function (err) {
        toast('Aktualisierung fehlgeschlagen', err.message);
        itemEl.dataset.busy = '';
        itemEl.style.opacity = '';
      });
      return;
    }

    var removeBtn = e.target.closest('[data-remove-line]');
    if (removeBtn && root.contains(removeBtn)) {
      e.preventDefault();
      var itemEl2 = removeBtn.closest('[data-key]');
      if (!itemEl2 || itemEl2.dataset.busy === '1') return;
      itemEl2.dataset.busy = '1';
      itemEl2.style.opacity = '0.5';
      changeLine(itemEl2.dataset.key, 0).catch(function (err) {
        toast('Entfernen fehlgeschlagen', err.message);
        itemEl2.dataset.busy = '';
        itemEl2.style.opacity = '';
      });
      return;
    }
  });

  /* ── Initial: echten Zählerstand laden ────────────────────────── */
  function init() { refreshCount(); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  window.EkoCart = { money: money, getCart: getCart, addItem: addItem, changeLine: changeLine, refreshCount: refreshCount, toast: toast };
})();


/* ════════════════════════════════════════════════════════════════
   UI-Module (Galerie, Filter, Sticky-CTA) — unverändert aus Design
   ════════════════════════════════════════════════════════════════ */

/* PDP Foto-Slider (Round 7 Welle 4) — arrows, counter, swipe, keyboard, looping */
(function() {
    var galleries = document.querySelectorAll('[data-pdp-gallery]');
    if (!galleries.length) return;
    var reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
    galleries.forEach(function(gallery) {
      var mainBox = gallery.querySelector('[data-pdp-main-box]');
      var mainImg = gallery.querySelector('[data-pdp-main-img]');
      var thumbsWrap = gallery.querySelector('[data-pdp-thumbs]');
      var thumbs = thumbsWrap ? thumbsWrap.querySelectorAll('.pdp-thumb') : [];
      var counter = gallery.querySelector('[data-pdp-counter]');
      var prevBtn = gallery.querySelector('[data-pdp-prev]');
      var nextBtn = gallery.querySelector('[data-pdp-next]');
      if (!mainBox || !mainImg || !thumbs.length) return;

      var images = Array.prototype.map.call(thumbs, function(t) { return t.dataset.img; });
      var idx = 0;
      function syncIdxFromSrc() {
        var current = mainImg.getAttribute('src') || '';
        for (var i = 0; i < images.length; i++) {
          if (current.indexOf(images[i].replace(/^\//, '')) !== -1) { idx = i; break; }
        }
      }
      syncIdxFromSrc();

      function set(i) {
        if (i < 0) i = images.length - 1;
        if (i >= images.length) i = 0;
        idx = i;
        var nextSrc = images[i];
        var current = mainImg.getAttribute('src') || '';
        if (current.indexOf(nextSrc.replace(/^\//, '')) === -1) {
          if (reduce) {
            mainImg.src = nextSrc;
          } else {
            mainBox.classList.add('is-fading');
            setTimeout(function() {
              mainImg.src = nextSrc;
              requestAnimationFrame(function() { mainBox.classList.remove('is-fading'); });
            }, 160);
          }
        }
        thumbs.forEach(function(t, ti) { t.classList.toggle('is-active', ti === i); });
        if (counter) counter.textContent = (i + 1) + ' / ' + images.length;
      }

      if (counter) counter.textContent = (idx + 1) + ' / ' + images.length;
      thumbs.forEach(function(t, ti) { t.classList.toggle('is-active', ti === idx); });

      if (prevBtn) prevBtn.addEventListener('click', function(e) { e.preventDefault(); set(idx - 1); });
      if (nextBtn) nextBtn.addEventListener('click', function(e) { e.preventDefault(); set(idx + 1); });

      thumbs.forEach(function(t, i) {
        t.addEventListener('click', function(e) { e.preventDefault(); set(i); });
      });

      mainBox.setAttribute('tabindex', '0');
      mainBox.addEventListener('keydown', function(e) {
        if (e.key === 'ArrowLeft') { e.preventDefault(); set(idx - 1); }
        else if (e.key === 'ArrowRight') { e.preventDefault(); set(idx + 1); }
      });

      var sx = 0, sy = 0, tracking = false;
      mainBox.addEventListener('touchstart', function(e) {
        if (e.touches.length !== 1) return;
        sx = e.touches[0].clientX; sy = e.touches[0].clientY; tracking = true;
      }, { passive: true });
      mainBox.addEventListener('touchend', function(e) {
        if (!tracking) return; tracking = false;
        var dx = e.changedTouches[0].clientX - sx;
        var dy = e.changedTouches[0].clientY - sy;
        if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) set(idx + (dx < 0 ? 1 : -1));
      }, { passive: true });

      // Expose API: size-tile image-swap calls this so the counter stays in sync
      function setByImg(src) {
        if (!src) return;
        var i = images.indexOf(src);
        if (i >= 0) set(i);
      }
      gallery._ekoGallery = { set: set, setByImg: setByImg };
    });

    // Listen for size-tile image-swap events
    document.addEventListener('eko-pdp-set-img', function(e) {
      var src = e.detail && e.detail.src;
      if (!src) return;
      galleries.forEach(function(g) {
        if (g._ekoGallery) g._ekoGallery.setByImg(src);
      });
    });
})();

/* Filter-Chips toggle + product-card show/hide (Round 8 / M3) */
(function() {
    var chips = document.querySelectorAll('.filter-chip');
    if (!chips.length) return;
    var cards = document.querySelectorAll('.shop-grid .product-card[data-filter]');
    if (!cards.length) return;
    function apply(filter) {
      cards.forEach(function(card) {
        var match = filter === 'alle' || card.dataset.filter === filter;
        card.style.display = match ? '' : 'none';
      });
    }
    chips.forEach(function(c) {
      c.addEventListener('click', function() {
        chips.forEach(function(x) {
          x.classList.remove('is-active');
          x.setAttribute('aria-selected', 'false');
        });
        c.classList.add('is-active');
        c.setAttribute('aria-selected', 'true');
        apply(c.dataset.filter);
        // Keep the chip visible in the horizontal scroller after tap
        if (typeof c.scrollIntoView === 'function') {
          c.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
        }
      });
    });
})();

/* Mobile Sticky-CTA (Round 8 / M1) — auto-bootstrap, IO-based show/hide, live price sync */
(function() {
    var msc = document.querySelector('[data-mobile-sticky-cta]');
    if (!msc) return;
    var srcAdd = document.querySelector('[data-pdp-add]');
    if (!srcAdd) { msc.parentNode && msc.parentNode.removeChild(msc); return; }

    var titleEl = msc.querySelector('[data-msc-title]');
    var priceEl = msc.querySelector('[data-msc-price]');
    var ppsEl   = msc.querySelector('[data-msc-pps]');
    var imgEl   = msc.querySelector('[data-msc-img]');
    var ctaBtn  = msc.querySelector('[data-msc-add]');

    var srcPrice   = document.querySelector('[data-pdp-current-price]');
    var srcPps     = document.querySelector('[data-pdp-pps]');
    var srcImg     = document.querySelector('[data-pdp-main-img]');
    var srcEyebrow = document.querySelector('.pdp-hero .eyebrow');
    var srcH1      = document.querySelector('.pdp-hero h1');

    function clean(t) { return (t || '').replace(/\s+/g, ' ').trim(); }
    function deriveTitle() {
      if (srcEyebrow) return clean(srcEyebrow.textContent).replace(/^Ekologa\s+/, '');
      if (srcH1) return clean(srcH1.textContent).split(/[·.,]/)[0];
      return '';
    }

    function sync() {
      if (priceEl && srcPrice) priceEl.innerHTML = srcPrice.innerHTML;
      if (ppsEl) {
        if (srcPps) {
          ppsEl.innerHTML = srcPps.innerHTML;
          ppsEl.style.display = '';
        } else {
          ppsEl.style.display = 'none';
        }
      }
      if (imgEl && srcImg) {
        var s = srcImg.getAttribute('src');
        if (s && imgEl.getAttribute('src') !== s) imgEl.setAttribute('src', s);
      }
      if (titleEl) {
        var t = deriveTitle();
        if (t) titleEl.textContent = t;
      }
    }
    sync();
    msc.hidden = false;

    var io = new IntersectionObserver(function(entries) {
      entries.forEach(function(e) {
        var below = !e.isIntersecting && e.boundingClientRect.top < 0;
        msc.classList.toggle('is-visible', below);
        msc.setAttribute('aria-hidden', below ? 'false' : 'true');
      });
    }, { threshold: 0 });
    io.observe(srcAdd);

    if (typeof MutationObserver !== 'undefined') {
      var mo = new MutationObserver(sync);
      if (srcPrice) mo.observe(srcPrice, { childList: true, characterData: true, subtree: true });
      if (srcPps)   mo.observe(srcPps,   { childList: true, characterData: true, subtree: true });
      if (srcImg)   mo.observe(srcImg,   { attributes: true, attributeFilter: ['src'] });
    }

    if (ctaBtn) {
      ctaBtn.addEventListener('click', function(e) {
        e.preventDefault();
        srcAdd.click();
        srcAdd.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    }
})();
